import type { ReactNode } from 'react';

import type { TemplateData } from '@xing-com/crate-common-graphql-types';
import { SkeletonImage } from '@xing-com/skeleton';

import { useExternalContentConsent } from '../../../hooks';
import { VideoConsentPlaceholder } from '../../no-consent-message';
import { ShowMore } from '../../show-more';
import { DESCRIPTION_PREVIEW_HEIGHT } from '../constants';

import { Image } from './image';
import { Section } from './section';
import { SocialLinks } from './social-links';
import { Video } from './video';

const getVideo = ({
  type,
  isLoading,
  hasConsent,
  url,
  jobId,
}: {
  type: 'youtube' | 'vimeo';
  isLoading: boolean;
  hasConsent?: boolean;
  url?: string | null;
  jobId: string;
}): ReactNode => {
  if (!url) return null;
  if (isLoading) return <SkeletonImage />;

  const provider = type === 'youtube' ? 'Youtube' : 'Vimeo';

  return url && hasConsent ? (
    <Video type={type} url={url} jobId={jobId} />
  ) : (
    <VideoConsentPlaceholder provider={provider} />
  );
};

type Props = {
  jobId: string;
  description: TemplateData;
  skipShowMore: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
};

type TemplateDataContentProps = {
  jobId: string;
  description: TemplateData;
};

const TemplateDataContent: React.FC<TemplateDataContentProps> = ({
  jobId,
  description,
}) => {
  const {
    userCentrics: { youtube, vimeo, isLoading },
  } = useExternalContentConsent();
  const {
    sectionTitleColor,
    headerImage,
    genericDescription,
    companyDescriptionTitle,
    companyDescriptionContent,
    responsibilityTitle,
    responsibilityContent,
    skillsTitle,
    skillsContent,
    weOfferTitle,
    weOfferContent,
    contactInfoTitle,
    contactInfoContent,
    footerImage,
    videoUrls,
    socialUrls,
  } = description;

  return (
    <>
      {headerImage ? (
        <Image src={headerImage} data-testid="header-image" />
      ) : null}
      {genericDescription ? (
        <Section jobId={jobId} content={genericDescription} />
      ) : null}
      {companyDescriptionContent ? (
        <Section
          jobId={jobId}
          title={companyDescriptionTitle ?? undefined}
          titleColor={sectionTitleColor ?? undefined}
          content={companyDescriptionContent}
        />
      ) : null}
      {responsibilityContent ? (
        <Section
          jobId={jobId}
          title={responsibilityTitle ?? undefined}
          titleColor={sectionTitleColor ?? undefined}
          content={responsibilityContent}
        />
      ) : null}
      {skillsContent ? (
        <Section
          jobId={jobId}
          title={skillsTitle ?? undefined}
          titleColor={sectionTitleColor ?? undefined}
          content={skillsContent}
        />
      ) : null}
      {weOfferContent ? (
        <Section
          jobId={jobId}
          title={weOfferTitle ?? undefined}
          titleColor={sectionTitleColor ?? undefined}
          content={weOfferContent}
        />
      ) : null}
      {contactInfoContent ? (
        <Section
          jobId={jobId}
          title={contactInfoTitle ?? undefined}
          titleColor={sectionTitleColor ?? undefined}
          content={contactInfoContent}
        />
      ) : null}
      {footerImage ? (
        <Image src={footerImage} data-testid="footer-image" />
      ) : null}
      {getVideo({
        type: 'vimeo',
        url: videoUrls?.vimeo,
        isLoading,
        hasConsent: vimeo?.hasConsent,
        jobId,
      })}
      {getVideo({
        type: 'youtube',
        url: videoUrls?.youtube,
        isLoading,
        hasConsent: youtube?.hasConsent,
        jobId,
      })}
      {socialUrls ? (
        <SocialLinks jobId={jobId} socialUrls={socialUrls} />
      ) : null}
    </>
  );
};

export const TemplateDataDescription: React.FC<Props> = ({
  jobId,
  description,
  skipShowMore,
  onExpand,
  onCollapse,
}) => {
  return skipShowMore ? (
    <TemplateDataContent jobId={jobId} description={description} />
  ) : (
    <ShowMore
      previewHeight={DESCRIPTION_PREVIEW_HEIGHT}
      expandLabelKey={'JOBS_JOB_DETAILS_DESCRIPTION_SHOW_MORE'}
      collapseLabelKey={'JOBS_JOB_DETAILS_DESCRIPTION_SHOW_LESS'}
      onExpand={onExpand}
      onCollapse={onCollapse}
      hideOnDesktop
      centered
    >
      <TemplateDataContent jobId={jobId} description={description} />
    </ShowMore>
  );
};
